import {
  ApiWorkflowBlockEdgeShort,
  ApiWorkflowBlockShort,
  ApiWorkflowListItem,
} from "@/types/workflows/workflow.types.ts";
import {
  ApiWorkflowBlockActionTypes,
  ApiWorkflowBlockTypes,
} from "@/types/workflows/workflow.base-enum.types.ts";
import { Stack, Text, Tooltip, TooltipContent, TooltipTrigger } from "@skailer-com/uikit";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

export const WorkflowTriggerCaption = ({
  workflow,
  triggerDescription,
}: {
  workflow: ApiWorkflowListItem;
  triggerDescription: string;
}) => {
  const { t } = useTranslation();

  const getActionTitle = (action: ApiWorkflowBlockShort) => {
    if (action.title) return action.title;

    const titleKeys: Partial<Record<ApiWorkflowBlockActionTypes, string>> = {
      [ApiWorkflowBlockActionTypes.send_notification]: "workflow:action_type_notification",
      [ApiWorkflowBlockActionTypes.http_request]: "workflow:action_type_http_request",
      [ApiWorkflowBlockActionTypes.google_workspace]: "workflow:action_type_google_workspace",
      [ApiWorkflowBlockActionTypes.invitation]: "workflow:action_type_invitation",
      [ApiWorkflowBlockActionTypes.survey]: "workflow:action_type_survey",
      [ApiWorkflowBlockActionTypes.task]: "workflow:action_type_task",
      [ApiWorkflowBlockActionTypes.performance_review]:
        "performance_review:create_performance_review",
      [ApiWorkflowBlockActionTypes.performance_review_enroll]:
        "workflow:action_type_performance_review_add_employee",
      [ApiWorkflowBlockActionTypes.survey_enroll]: "workflow:action_type_survey_enroll",
      [ApiWorkflowBlockActionTypes.run_workflow]: "workflow:action_type_run_workflow",
      [ApiWorkflowBlockActionTypes.survey_with_approval]: "workflow:action_type_approval",
      [ApiWorkflowBlockActionTypes.esign]: "workflow:action_type_esign",
      [ApiWorkflowBlockActionTypes.meeting]: "workflow:action_type_meeting",
      [ApiWorkflowBlockActionTypes.candidate_email]: "workflow:action_type_candidate_email",
      [ApiWorkflowBlockActionTypes.candidate_stage_change]:
        "workflow:action_type_candidate_stage_change",
    };
    const titleKey = titleKeys[action.action_type];
    return titleKey ? t(titleKey) : t("workflow:actions");
  };

  const outgoingEdges = new Map<number, ApiWorkflowBlockEdgeShort[]>();
  workflow.edges.forEach((edge) => {
    const edges = outgoingEdges.get(edge.from_block_id) ?? [];
    edges.push(edge);
    outgoingEdges.set(edge.from_block_id, edges);
  });
  outgoingEdges.forEach((edges) => edges.sort((a, b) => a.priority - b.priority));

  type ActionTreeNode = { action: ApiWorkflowBlockShort; children: ActionTreeNode[] };
  const actionsById = new Map(
    workflow.actions
      .filter((action) => action.block_type === ApiWorkflowBlockTypes.action)
      .map((action) => [action.id, action])
  );
  const visited = new Set<number>();
  const buildActionNode = (action: ApiWorkflowBlockShort): ActionTreeNode => {
    const node: ActionTreeNode = { action, children: [] };
    if (visited.has(action.id)) return node;
    visited.add(action.id);

    node.children = (outgoingEdges.get(action.id) ?? [])
      .map((edge) => actionsById.get(edge.to_block_id))
      .filter((child): child is ApiWorkflowBlockShort => child != undefined)
      .map(buildActionNode);
    return node;
  };

  const rootActionIds = workflow.start_block_id
    ? (outgoingEdges.get(workflow.start_block_id) ?? []).map((edge) => edge.to_block_id)
    : workflow.actions
        .filter((action) => !workflow.edges.some((edge) => edge.to_block_id === action.id))
        .map((action) => action.id);
  const actionTree = rootActionIds
    .map((actionId) => actionsById.get(actionId))
    .filter((action): action is ApiWorkflowBlockShort => action != undefined)
    .map(buildActionNode);

  const renderActionList = (nodes: ActionTreeNode[], level = 0): ReactNode => (
    <ol
      className={level === 0 ? "list-inside list-decimal" : "ml-4 list-inside list-[lower-alpha]"}
    >
      {nodes.map((node) => (
        <li key={node.action.id}>
          {getActionTitle(node.action)}
          {node.children.length > 0 && renderActionList(node.children, level + 1)}
        </li>
      ))}
    </ol>
  );

  const actionsCaption = t("plurals:actions_count", { count: workflow.action_count });
  const caption = [triggerDescription !== "-" ? triggerDescription : null, actionsCaption]
    .filter(Boolean)
    .join(" → ");
  const hasWhen = triggerDescription !== "-";
  const hasRun = actionTree.length > 0;
  const captionElement = <span className="text-secondary truncate">{caption}</span>;

  if (!hasWhen && !hasRun) return captionElement;

  return (
    <Tooltip delay={false}>
      <TooltipTrigger asChild={true}>{captionElement}</TooltipTrigger>
      <TooltipContent className="max-w-[360px] p-3">
        <Stack gap="md" className={"text-sm leading-4"}>
          {hasWhen && (
            <Stack gap="xs">
              <Text className="font-semibold">{t("workflow:when")}</Text>
              <Text>{triggerDescription}</Text>
            </Stack>
          )}
          {hasRun && (
            <Stack gap="xs">
              <Text className="font-semibold">{t("workflow:run")}</Text>
              {renderActionList(actionTree)}
            </Stack>
          )}
        </Stack>
      </TooltipContent>
    </Tooltip>
  );
};
